/** One teachable board per illustrated slide. Each row names the item and the handling rule. */
type Tone = "public" | "internal" | "confidential" | "restricted" | "risk" | "safe";

interface BoardRow {
  name: string;
  detail?: string;
  tag: string;
  tone: Tone;
}

const BOARDS: Record<string, { caption: string; rows: BoardRow[] }> = {
  "ch1-maya": {
    caption: "Classify the contents, not the label",
    rows: [
      { name: "Account overview", detail: "No confidentiality label", tag: "Unlabelled", tone: "internal" },
      { name: "Customer names and contacts", detail: "Identifiable people", tag: "Personal", tone: "confidential" },
      { name: "Negotiated discounts", detail: "Not public pricing", tag: "Confidential", tone: "confidential" },
      { name: "Visiting supplier", detail: "No need for this file", tag: "Not authorised", tone: "risk" },
    ],
  },
  "ch1-classes": {
    caption: "What each document is",
    rows: [
      { name: "Public brochure", detail: "Already approved for release", tag: "Public", tone: "public" },
      { name: "Account overview", detail: "Names and negotiated discounts", tag: "Confidential", tone: "confidential" },
      { name: "Applicant CV", detail: "Contact details and interview notes", tag: "Personal", tone: "confidential" },
      { name: "Draft supplier contract", detail: "Negotiated prices", tag: "Confidential", tone: "confidential" },
    ],
  },
  "ch2-fields": {
    caption: "Ask only what the task needs",
    rows: [
      { name: "Name and work contact", detail: "Required to register the person", tag: "Needed", tone: "safe" },
      { name: "Health history", detail: "Added to every application", tag: "Higher-risk", tone: "restricted" },
      { name: "Might be useful later", detail: "No present purpose", tag: "Do not collect", tone: "risk" },
    ],
  },
  "ch3-access": {
    caption: "Whose access is this?",
    rows: [
      { name: "Borrowed password", detail: "Supplier record later changed", tag: "No clear record", tone: "risk" },
      { name: "Your own account", detail: "Actions stay attributable", tag: "Use this", tone: "safe" },
      { name: "Sign-in you did not start", detail: "Message claims to be support", tag: "Deny and report", tone: "restricted" },
    ],
  },
  "ch4-store": {
    caption: "Where the complaint file sits",
    rows: [
      { name: "Personal email and tablet", detail: "Backup the organisation cannot reach", tag: "Unapproved", tone: "risk" },
      { name: "Case system", detail: "Where colleagues expect the file", tag: "Approved", tone: "safe" },
      { name: "Copy left at home", detail: "Team keeps using the older version", tag: "Split record", tone: "confidential" },
    ],
  },
  "ch5-share": {
    caption: "What actually leaves",
    rows: [
      { name: "Visible quotation", detail: "The page the supplier needs", tag: "Share this", tone: "safe" },
      { name: "Hidden sheet", detail: "Other customers and margins", tag: "Confidential", tone: "confidential" },
      { name: "Link anyone can edit", detail: "Full workbook, not a clean copy", tag: "Too much access", tone: "risk" },
    ],
  },
  "ch6-verify": {
    caption: "Check person, permission, destination",
    rows: [
      { name: "Lookalike director address", detail: "One character off the real domain", tag: "Identity unproven", tone: "risk" },
      { name: "Applicant CVs", detail: "Contact details and interviews", tag: "Personal", tone: "confidential" },
      { name: "New consultant", detail: "Not approved for recruitment data", tag: "No authority", tone: "restricted" },
      { name: "Known directory number", detail: "Not the number in the email", tag: "Verify here", tone: "safe" },
    ],
  },
  "ch7-ai": {
    caption: "What the public tool receives",
    rows: [
      { name: "Customer name removed", detail: "The only change Sam made", tag: "Not enough", tone: "internal" },
      { name: "Order, fault, delivery place", detail: "Together they still identify someone", tag: "Personal", tone: "confidential" },
      { name: "Public AI assistant", detail: "Not approved for this transcript", tag: "Disclosure", tone: "risk" },
      { name: "Summary adds a refund", detail: "Never said on the call", tag: "Unchecked", tone: "restricted" },
    ],
  },
  "ch8-around": {
    caption: "Who else can see or hear",
    rows: [
      { name: "Headphones", detail: "Hide the other side of the call only", tag: "Not enough", tone: "internal" },
      { name: "Dispute and product date", detail: "Spoken in a busy café", tag: "Overheard", tone: "confidential" },
      { name: "Laptop left unlocked", detail: "Screen faces the counter", tag: "Visible", tone: "risk" },
      { name: "Private room, or wait", detail: "Sensitive parts can be postponed", tag: "Do this", tone: "safe" },
    ],
  },
  "ch9-record": {
    caption: "Which address is the real one?",
    rows: [
      { name: "Customer system", detail: "Address corrected by the other team", tag: "Authoritative", tone: "safe" },
      { name: "Laptop delivery list", detail: "Still shows the former address", tag: "Stale copy", tone: "risk" },
      { name: "Dispatch used the laptop", detail: "Parcel went to the old address", tag: "Wrong record", tone: "confidential" },
    ],
  },
  "ch10-retain": {
    caption: "What is left after the project",
    rows: [
      { name: "Official project file", detail: "Final notes were never added", tag: "Incomplete", tone: "internal" },
      { name: "Laptop export", detail: "Participant details still on the device", tag: "Extra copy", tone: "confidential" },
      { name: "Private folder", detail: "Second copy nobody handed over", tag: "Extra copy", tone: "risk" },
      { name: "Approved handover", detail: "Keep the required record, remove the rest", tag: "Do this", tone: "safe" },
    ],
  },
  "ch11-request": {
    caption: "A privacy request in ordinary words",
    rows: [
      { name: "Send me everything you hold", detail: "No legal wording required", tag: "Access request", tone: "confidential" },
      { name: "Correct my old address", detail: "Asked in the same message", tag: "Correction", tone: "internal" },
      { name: "CRM screenshot", detail: "Also shows another customer", tag: "Do not send", tone: "risk" },
      { name: "Record and route", detail: "The privacy team decides the outcome", tag: "Do this", tone: "safe" },
    ],
  },
  "ch12-report": {
    caption: "What waiting costs",
    rows: [
      { name: "Complaint file", detail: "Sent to the wrong external address", tag: "Incident", tone: "confidential" },
      { name: "Wait until morning", detail: "Hope they never open it", tag: "Too late", tone: "risk" },
      { name: "Downloaded overnight", detail: "Recall time already used up", tag: "Exposed", tone: "restricted" },
      { name: "Report when you notice", detail: "Preserve the facts, follow instructions", tag: "Do this", tone: "safe" },
    ],
  },
};

export function ChapterArt({ id }: { id: string }) {
  const board = BOARDS[id];
  if (!board) return null;
  return (
    <div className="cart cart-board">
      <div className="cart-caption">{board.caption}</div>
      {board.rows.map((row, index) => (
        <div className={`cart-row tone-${row.tone}`} key={row.name} style={{ animationDelay: `${index * 140}ms` }}>
          <div className="cart-row-copy">
            <b>{row.name}</b>
            {row.detail && <span>{row.detail}</span>}
          </div>
          <em>{row.tag}</em>
        </div>
      ))}
    </div>
  );
}
